/**
 * Auth Mode Configuration
 *
 * Determines which authentication methods are available:
 *   - "okta"    → Direct Okta SPA auth only
 *   - "cognito" → Cognito email/password only
 *   - "dual"    → Both Okta and Cognito; user chooses at login
 *
 * In dual mode:
 *   - @nepgroup.com users must use Okta
 *   - Only @nepgroup.com users can be Super Admins
 *   - External users can use Cognito email/password
 */

export type AuthMode = "okta" | "cognito" | "dual";

export const AUTH_MODE: AuthMode =
  (import.meta.env.VITE_AUTH_MODE as AuthMode) || "dual";

export const isOktaMode = AUTH_MODE === "okta";
export const isCognitoMode = AUTH_MODE === "cognito";
export const isDualMode = AUTH_MODE === "dual";
export const isOktaAvailable = AUTH_MODE === "okta" || AUTH_MODE === "dual";
export const isCognitoAvailable = AUTH_MODE === "cognito" || AUTH_MODE === "dual";

/** Check if an email is a NEP internal email that requires Okta */
export function isNepEmail(email: string): boolean {
  return email.toLowerCase().endsWith("@nepgroup.com");
}

/**
 * Reduce a URL to its origin (scheme + host [+ port]), dropping path/query.
 * e.g. https://host/whep/test-h265-5003 -> https://host
 */
export function toOrigin(url: string | undefined | null): string {
  if (!url) return "";
  try {
    return new URL(url).origin;
  } catch {
    const withoutQuery = url.split(/[?#]/)[0];
    const m = withoutQuery.match(/^([a-z]+:\/\/[^/]+)/i);
    return m ? m[1] : url;
  }
}

/**
 * Display a stream URL according to the viewer's email domain: full URL for
 * @nepgroup.com users, origin-only for everyone else. Mirrors the backend
 * redaction; used for admin URL *display* surfaces (not playback).
 */
export function displayStreamUrl(
  url: string | undefined | null,
  viewerEmail: string | undefined | null,
): string {
  if (!url) return "";
  return viewerEmail && isNepEmail(viewerEmail) ? url : toOrigin(url);
}
